<template>
    <div
        @wheel="handleWheel"
        @mousemove="handleMove"
        @mousedown="mousePressed = true"
        @mouseup="mousePressed = false"
        class="diagram-viewer"
        :style="{ cursor: mousePressed ? 'grabbing' : 'grab' }"
    >
        <div class="wrapper" :style="{ transform }">
            <BlockConnectors
                :blocks="blocks.blocks"
                :metas="blocks.metas"
                :test-state="testState"
                :test-start-block-id="testStartBlockId"
            />
            <Block
                @openContextMenu="(e) => $emit('openContextMenu', e)"
                v-for="block in blocks.blocks"
                :key="block.id"
                :block="block"
                :metas="blocks.metas"
                :active="selectedBlock === block.id"
                @focusBlock="focusBlock"
                :board="board"
                :storyId="storyId"
                :storyVersion="storyVersion"
                :flattedDiagram="flattedDiagram"
                :movingMode="movingMode"
                :movingBlock="movingBlock"
                :changeMoveMode="changeMoveMode"
                :moveBlock="moveBlock"
                :copyingMode="copyingMode"
                :copyingBlock="copyingBlock"
                :changeCopyMode="changeCopyMode"
                :copyBlock="copyBlock"
                :showNotification="showNotification"
                :dragMode="dragMode"
                :addBlockToDiagram="addBlockToDiagram"
                :draggingType="draggingType"
                :collapsed="board.state.collapsed.includes(block.id)"
                :collapsedAll="board.state.collapsed.includes(block.id)"
                :start-test-from-here="startTestFromHere"
                :test-position="$_.get(testState, 'position')"
                :test-closed="$_.get(testState, 'closed', false)"
                :selected="selectedBlockId === block.id"
                :preview-mode="previewMode"
                :getAllowedBlocks="getAllowedBlocks"
            />
        </div>
    </div>
</template>

<script>
import { log2 } from "../../../../../../utils/log2";
import _ from "lodash";
import { mapGetters } from "vuex";
import BlockConnectors from "./BlockConnectors";
import Block from "./Block";
import BLOCK_TYPES from "../../../block.types";
import * as d3 from "d3";
import SYSTEM_PUBSUB from "../../../../../../utils/systemPubSub";
import { filter } from "rxjs/operators";

const log = log2("DiagramViewer");
export default {
    name: "DiagramViewer",
    props: [
        "board",
        "diagram",
        "scale",
        "translate",
        "selectedBlock",
        "navigation",
        "navigateToBlock",
        "storyId",
        "storyVersion",
        "movingMode",
        "movingBlock",
        "changeMoveMode",
        "moveBlock",
        "copyingMode",
        "copyingBlock",
        "changeCopyMode",
        "copyBlock",
        "flattedDiagram",
        "showNotification",
        "dragMode",
        "addBlockToDiagram",
        "draggingType",
        "startTestFromHere",
        "testState",
        "selectedBlockId",
        "customAttributes",
        "previewMode",
        "testStartBlockId",
        "getAllowedBlocks",
    ],
    components: { Block, BlockConnectors },
    data() {
        return {
            mousePressed: false,
            sub: null,
        };
    },

    computed: {
        ...mapGetters({
            settings: "CB/settings",
        }),

        transform() {
            return `scale(${this.scale}) translate(${this.translate.x}px, ${this.translate.y}px)`;
        },
        blocks: function () {
            const startPoint = { x: 100, y: 300 };
            const blockSizes = { width: 190, height: 40 };
            const squareBlockSizes = { width: 40, height: 40 };
            const squareBlockTypes = [BLOCK_TYPES.USER_INPUT, BLOCK_TYPES.ATTACHMENT_INPUT];
            const rootBlock = this.navigation.selectedFlowPath
                ? _.get(this.diagram, this.navigation.selectedFlowPath)
                : this.diagram;

            const metas = {
                [rootBlock.id]: {
                    width: squareBlockTypes.includes(rootBlock.type) ? squareBlockSizes.width : blockSizes.width,
                    height: squareBlockTypes.includes(rootBlock.type) ? squareBlockSizes.height : blockSizes.height,
                    name: rootBlock.name,
                    isRoot: true,
                    isSquare: squareBlockTypes.includes(rootBlock.type),
                },
            };
            const rootMeta = metas[rootBlock.id];

            const flatBlock = (block, parentMeta, isRoot) => {
                const isSquare = squareBlockTypes.includes(block.type);
                const sizes = isSquare ? squareBlockSizes : blockSizes;
                const meta = isRoot
                    ? parentMeta
                    : {
                          width: sizes.width,
                          height: sizes.height,
                          name: block.name,
                          isRoot: false,
                          isSquare,
                      };

                metas[block.id] = meta;
                // log("metas", metas);
                const nextFlow = !isRoot && block.type === "FLOW";

                const items = [
                    block,
                    ...(nextFlow ||
                    (this.board.state.collapsed.includes(block.id) && !["START_POINT"].includes(block.type))
                        ? []
                        : block.children
                              .map((childBlock, i) => {
                                  return !!childBlock ? flatBlock(childBlock, meta, false) : null;
                              })
                              .filter(Boolean)),
                ];

                return _.flatten(items);
            };

            const items = _.flatten(flatBlock(rootBlock, rootMeta, true));

            const calculatePositions = () => {
                const root = _.cloneDeep(rootBlock);
                const flatFlows = (block, isRoot = false) => {
                    if (!isRoot && block.type === BLOCK_TYPES.FLOW) block.children = [];
                    block.children = block.children.filter(Boolean);
                    block.children.filter(Boolean).forEach(flatFlows);
                };
                flatFlows(root, true);

                console.log("calculatePositions", root);
                let treemap = d3.tree().nodeSize([blockSizes.height * 2.5, blockSizes.width + 80]);

                let nodes = d3.hierarchy(root, function (d) {
                    return d.children;
                });
                nodes = treemap(nodes);
                // log("nodes", nodes);
                const traverse = (block) => {
                    // log("traverse", block, block.children);
                    if (!!metas[block.data.id]) {
                        metas[block.data.id].x = block.y;
                        metas[block.data.id].y = block.x;
                    }
                    (block.children || []).filter(Boolean).forEach((child) => {
                        traverse(child);
                    });
                };
                traverse(nodes);
            };
            calculatePositions();

            this.$emit("blockMetasCalculated", metas);
            const aiAssistantBlock = {
                id: "AI_ASSISTANT",
                name: "",
                type: "AI_ASSISTANT",
                children: [],
            };
            metas[aiAssistantBlock.id] = {
                width: 190,
                height: 40,
                name: "",
                isRoot: false,
                isSquare: false,
                x: 0,
                y: 100,
            };
            log("metas", {
                metas,
                items,
                aiAssistantBlock,
            });
            return {
                blocks: [aiAssistantBlock, ...items],
                metas,
            };
        },
    },
    methods: {
        handleWheel(e) {
            e.preventDefault();
            const settings = this.settings(this.storyId);
            log("handleWheel", e);
            log("handleWheel", { x: -e.deltaX, y: -e.deltaY, ctrlKey: e.ctrlKey, settings });
            if (settings.navigation === "mouse" || e.ctrlKey) {
                // this.$emit("updateScale", e.deltaY * 0.05);
                const currentScale = this.scale;

                let value = Math.min(200, Math.abs(e.deltaY));
                //scale 200  to 0.5 - 1.5
                let newDelta = (value / 200 + 0.5) / 10;
                newDelta = e.deltaY <= 0 ? newDelta : newDelta * -1;
                const nextScale = currentScale + newDelta;
                log("updateScale calling", {
                    deltaY: e.deltaY,
                    currentScale,
                    value,
                    newDelta,
                    nextScale,
                });
                // const smoothScale = (oldScale, newScale) => {
                //     const diff = newScale - oldScale;
                //     const step = diff / 10;
                //
                //     for (let i = 0; i < 10; i++) {
                //         setTimeout(() => {
                //             this.$emit("updateScale", oldScale + step * (i + 1));
                //         }, 20);
                //     }
                // };
                // smoothScale(currentScale, nextScale);
                this.$emit("updateScale", nextScale);
            } else {
                log("updateTranslate calling");
                this.$emit("updateTranslate", { x: -e.deltaX, y: -e.deltaY });
            }
        },
        handleMove(e) {
            let movement = {
                x: e.movementX,
                y: e.movementY,
            };
            e.preventDefault();
            if (this.mousePressed) this.$emit("updateTranslate", movement);
        },
        focusBlock(e, block) {
            log("focusBlock block", block);
            if (
                [
                    BLOCK_TYPES.START_POINT,
                    BLOCK_TYPES.DEFAULT_FALLBACK,
                    BLOCK_TYPES.SUCCESS,
                    BLOCK_TYPES.FAILURE,
                    BLOCK_TYPES.FALLBACK,
                    BLOCK_TYPES.CLOSE_CHAT,
                    BLOCK_TYPES.TRANSFER_CHAT,
                ].includes(block.type)
            )
                return;
            if (block.type === "FLOW") {
                this.navigateToBlock(block);
                return;
            }
            this.$emit("openModal", block);
            log("focusBlock x, y");
            const { x, y } = this.blocks.metas[block.id];
            this.$emit("setTranslate", {
                x: -x + window.innerWidth / 2 - 250,
                y: -y + window.innerHeight / 2 - 50,
            });
        },
    },
    mounted() {
        this.$emit("blocks-metas", this.blocks.metas);
        this.sub = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.CB_FOCUS_BLOCK))
            .subscribe(({ payload }) => {
                log("mounted blocks focusBlock start");
                if (!!this.blocks.metas[payload.blockId]) {
                    log("mounted blocks focusBlock start if", {
                        b: this.blocks,
                        m: this.blocks.metas,
                        mp: this.blocks.metas[payload.blockId],
                        p: payload,
                    });
                    const { x, y } = this.blocks.metas[payload.blockId];
                    log("mounted blocks focusBlock x, y", { x, y });
                    this.$emit("setTranslate", {
                        x: -x + window.innerWidth / 2 - 250,
                        y: -y + window.innerHeight / 2 - 50,
                    });
                }
            });
    },
    updated() {
        log("updated");
    },
    beforeDestroy() {
        this.sub.unsubscribe();
    },
    watch: {
        board: {
            handler() {
                log("updated board");
            },
            deep: true,
        },
        diagram: {
            handler() {
                log("updated diagram");
            },
            deep: true,
        },
        scale: {
            handler() {
                log("updated scale");
            },
            deep: true,
        },
        translate: {
            handler() {
                log("updated translate");
            },
            deep: true,
        },
        selectedBlock: {
            handler() {
                log("updated selectedBlock");
            },
            deep: true,
        },
        navigation: {
            handler(oldVal, newVal) {
                log("updated navigation", { oldVal, newVal });
            },
        },
        navigateToBlock: {
            handler() {
                log("updated navigateToBlock");
            },
        },
        storyId: {
            handler() {
                log("updated storyId");
            },
        },
        storyVersion: {
            handler() {
                log("updated storyVersion");
            },
        },
        movingMode: {
            handler() {
                log("updated movingMode");
            },
        },
        movingBlock: {
            handler() {
                log("updated movingBlock");
            },
            deep: true,
        },
        changeMoveMode: {
            handler() {
                log("updated changeMoveMode");
            },
        },
        moveBlock: {
            handler() {
                log("updated moveBlock");
            },
        },
        copyingMode: {
            handler() {
                log("updated copyingMode");
            },
        },
        copyingBlock: {
            handler() {
                log("updated copyingBlock");
            },
        },
        changeCopyMode: {
            handler() {
                log("updated changeCopyMode");
            },
        },
        copyBlock: {
            handler() {
                log("updated copyBlock");
            },
        },
        flattedDiagram: {
            handler() {
                log("updated flattedDiagram");
            },
        },
        showNotification: {
            handler() {
                log("updated showNotification");
            },
        },
        dragMode: {
            handler() {
                log("updated dragMode");
            },
        },
        addBlockToDiagram: {
            handler() {
                log("updated addBlockToDiagram");
            },
        },
        draggingType: {
            handler() {
                log("updated draggingType");
            },
        },
        startTestFromHere: {
            handler() {
                log("updated startTestFromHere");
            },
        },
        testState: {
            handler() {
                log("updated testState");
            },
            deep: true,
        },
        selectedBlockId: {
            handler() {
                log("updated selectedBlockId");
            },
        },
        customAttributes: {
            handler() {
                log("updated customAttributes");
            },
            deep: true,
        },
        previewMode: {
            handler() {
                log("updated previewMode");
            },
        },
        testStartBlockId: {
            handler() {
                log("updated testStartBlockId");
            },
        },
        getAllowedBlocks: {
            handler() {
                log("updated getAllowedBlocks");
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.diagram-viewer {
    //height: calc(100vh - 50px);
    height: calc(100vh);

    .wrapper {
        height: 100%;
        width: 100%;
    }
}
</style>
